import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, LogOut, ShieldCheck, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";

const AccountMenu = ({ scrolled }) => {
  const { user, logout, setAuthOpen } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const textCls = scrolled ? "text-foreground/90" : "text-white/90";

  if (user === null) return <span className="w-9 h-9" />;

  if (!user) {
    return (
      <button
        data-testid="signin-btn"
        onClick={() => setAuthOpen(true)}
        className={`flex items-center gap-2 text-xs font-sans font-semibold uppercase tracking-widest hover:text-gold transition-colors border border-gold/25 rounded-sm px-3 py-2 ${textCls}`}
      >
        <User size={15} />
        <span className="hidden sm:inline">{t("auth.signin")}</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        data-testid="account-btn"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        className={`flex items-center gap-2 hover:text-gold transition-colors ${textCls}`}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-gold/40" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-gold/15 text-gold flex items-center justify-center text-sm font-serif">
            {(user.name || user.email || "?")[0].toUpperCase()}
          </span>
        )}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-52 bg-popover border border-gold/20 rounded-sm overflow-hidden shadow-2xl"
          >
            <div className="px-4 py-3 border-b border-border">
              <div className="text-sm font-semibold text-foreground truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
            {user.role === "admin" && (
              <Link
                to="/admin"
                data-testid="admin-link"
                className="flex items-center gap-2 px-4 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-gold transition-colors"
              >
                <ShieldCheck size={16} /> Admin
              </Link>
            )}
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-gold transition-colors"
            >
              <LogOut size={16} /> {t("auth.logout")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountMenu;
